import { StrictMode } from "react"
import { createRoot } from "react-dom/client"
import "@/v2-backoffice-client/sentry"
import "@/v2-ui/css/index.css"
import { logError, logWarning } from "@/v2-ui/utils/utils.log"
import App from "@/v2-backoffice-client/app/App"
import "@/v2-ui/css/utilities.css"
import "@/v2-backoffice-client/styles.css"
import { msalReady } from "@/v2-backoffice-client/auth/auth.instance"

// A deploy replaces the hashed chunks a tab that is already open still points
// at; its next lazy route then fails with "Failed to fetch dynamically
// imported module" (V2-BACKOFFICE-CLIENT-2K). Vite raises this event for
// exactly that failure; a reload picks up the new index. The timestamp guard
// stops a loop when the reload itself cannot load.
const CHUNK_RELOAD_AT_KEY = "bo:chunk-reload-at"
window.addEventListener("vite:preloadError", (event) => {
  const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_AT_KEY) ?? 0)
  if (Date.now() - lastReloadAt < 10_000) return
  sessionStorage.setItem(CHUNK_RELOAD_AT_KEY, String(Date.now()))
  event.preventDefault()
  window.location.reload()
})

// An ArcGIS request error the SDK raised on its own (the Legend widget and
// layer-view creation fetch a layer's service independently of our
// `layer.load().catch(...)`) reaches the window as an unhandled rejection
// of a plain object `{ name: "request:server", message, details }` with no
// stack (V2-BACKOFFICE-CLIENT-2J, 45 events). It means one layer service was
// unreachable, which the preview already shows in words; nothing in our code
// can catch it earlier. Only that exact shape is claimed here, as a warning
// with the URL; every other rejection still surfaces as an error.
type EsriRequestError = {
  name?: string
  message?: string
  details?: { url?: string; httpStatus?: number }
}
function isEsriRequestError(reason: unknown): reason is EsriRequestError {
  if (typeof reason !== "object" || reason === null) return false
  const { name, details } = reason as EsriRequestError
  return typeof name === "string" && name.startsWith("request:") && !!details
}
window.addEventListener("unhandledrejection", (event) => {
  if (!isEsriRequestError(event.reason)) return
  event.preventDefault()
  logWarning(
    `arcgis ${event.reason.name}: ${event.reason.message ?? "request failed"}`,
    {
      level: "warning",
      extra: {
        url: event.reason.details?.url,
        httpStatus: event.reason.details?.httpStatus,
      },
    },
  )
})

const el = document.querySelector<HTMLDivElement>("#root-bo")
if (el) {
  const root = createRoot(el)
  // Render only after MSAL is initialized (and any login redirect is
  // consumed): msal-browser v5 throws uninitialized_public_client_application
  // on ANY account access before initialize() resolves, so an ungated render
  // lets first-mount effects (AuthLoader's token refresh) race it. Fail-open
  // on initialization errors — the app's auth flows recover via the normal
  // interactive login path.
  void msalReady
    .catch((error) => logError(error))
    .then(() => {
      root.render(
        <StrictMode>
          <App />
        </StrictMode>,
      )
    })
}
